import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Save, Sparkles, Send, Mail } from "lucide-react";
import { RispostaBadge } from "@/lib/inviti";
import { eur } from "@/lib/constants";

export const SPONSOR_TIPI = ["Ente", "Associazione", "Convention", "Sponsor", "Location", "Altro"];
export const emptyExperience = { nome: "", prezzo: 0, tipo: "", descrizione: "", colore: "#9333EA", data: "", ora: "", durata_min: 240, luogo: "", posti: 0, collaboratore_id: "", programma: [], sponsor: [], costi: [] };

const uid = () => crypto.randomUUID?.() || String(Date.now() + Math.random());

function RowList({ rows, setRows, fields, addLabel, tid, readOnly }) {
  const set = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      {rows.length === 0 && <p className="text-xs text-slate-400">Nessuna voce.</p>}
      {rows.map((r, i) => (
        <div key={r.id || i} data-testid={`${tid}-row-${i}`} className="flex items-center gap-2">
          {fields.map((f) => f.options ? (
            <Select key={f.k} value={r[f.k] || f.options[0]} onValueChange={(v) => set(i, f.k, v)} disabled={readOnly}>
              <SelectTrigger className={`h-9 ${f.w}`}><SelectValue /></SelectTrigger>
              <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input key={f.k} data-testid={`${tid}-${f.k}-${i}`} disabled={readOnly} type={f.type || "text"} placeholder={f.ph} value={r[f.k] ?? ""}
              onChange={(e) => set(i, f.k, e.target.value)} className={`h-9 ${f.w}`} />
          ))}
          {!readOnly && <button data-testid={`${tid}-del-${i}`} onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-slate-300 hover:text-destructive p-1"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
      {!readOnly && (
        <Button data-testid={`${tid}-add`} variant="outline" size="sm" className="rounded-full gap-1 border-dashed" onClick={() => setRows([...rows, { id: uid(), ...Object.fromEntries(fields.map((f) => [f.k, f.options ? f.options[0] : f.type === "number" ? 0 : ""])) }])}>
          <Plus className="h-3.5 w-3.5" /> {addLabel}
        </Button>
      )}
    </div>
  );
}

export function ExperienceDialog({ open, onOpenChange, initial, onSave, onInvite, isTitolare, collaboratori, tipi, partecipanti, saving }) {
  const [form, setForm] = useState(emptyExperience);
  useEffect(() => { if (open) setForm({ ...emptyExperience, ...(initial || {}) }); }, [open, initial?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const rsvp = Object.values(initial?.rsvp || {}).reduce((a, r) => ({ ...a, [r.email]: r.risposta }), {});
  const conEmail = (partecipanti || []).filter((p) => (p.email || "").trim()).length;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ro = !isTitolare;
  const totSponsor = (form.sponsor || []).reduce((a, x) => a + (Number(x.contributo) || 0), 0);
  const totCosti = (form.costi || []).reduce((a, x) => a + (Number(x.importo) || 0), 0);
  const incassi = (partecipanti || []).filter((p) => p.stage === "chiuso").reduce((a, p) => a + (p.ricavo || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="progetto-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> {initial?.id ? form.nome || "Experience" : "Nuova Portrait Experience"}</DialogTitle>
          <DialogDescription>Portrait day in luoghi particolari: shooting, visite guidate, aperitivi e partner del progetto.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="dettagli">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="dettagli" data-testid="exp-tab-dettagli">Dettagli</TabsTrigger>
            <TabsTrigger value="programma" data-testid="exp-tab-programma">Programma</TabsTrigger>
            <TabsTrigger value="sponsor" data-testid="exp-tab-sponsor">Partner & Sponsor</TabsTrigger>
            {initial?.id && <TabsTrigger value="partecipanti" data-testid="exp-tab-partecipanti">Partecipanti</TabsTrigger>}
            {isTitolare && <TabsTrigger value="costi" data-testid="exp-tab-costi">Costi & Margine</TabsTrigger>}
          </TabsList>

          <TabsContent value="dettagli" className="grid gap-4 pt-3">
            <div className="grid gap-1.5"><Label>Nome experience *</Label><Input data-testid="progetto-nome" disabled={ro} value={form.nome} onChange={(e) => set("nome", e.target.value)} placeholder="es. Portrait Day al Castello" /></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="grid gap-1.5"><Label>Data</Label><Input data-testid="progetto-data" disabled={ro} type="date" value={form.data || ""} onChange={(e) => set("data", e.target.value)} /></div>
              <div className="grid gap-1.5"><Label>Posti disponibili</Label><Input data-testid="progetto-posti" disabled={ro} type="number" value={form.posti} onChange={(e) => set("posti", e.target.value)} /></div>
              <div className="grid gap-1.5"><Label>Prezzo a persona (€)</Label><Input data-testid="progetto-prezzo" disabled={ro} type="number" value={form.prezzo} onChange={(e) => set("prezzo", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Ora inizio</Label><Input data-testid="progetto-ora" disabled={ro} type="time" value={form.ora || ""} onChange={(e) => set("ora", e.target.value)} /></div>
              <div className="grid gap-1.5"><Label>Durata (min)</Label><Input data-testid="progetto-durata" disabled={ro} type="number" step="30" value={form.durata_min ?? ""} onChange={(e) => set("durata_min", e.target.value)} /></div>
            </div>
            <div className="grid gap-1.5"><Label>Luogo / ambientazione</Label><Input data-testid="progetto-luogo" disabled={ro} value={form.luogo || ""} onChange={(e) => set("luogo", e.target.value)} placeholder="es. Villa storica, Bologna" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Tipo</Label>
                <Select value={form.tipo || "none"} disabled={ro} onValueChange={(v) => set("tipo", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="progetto-tipo"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{(tipi || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Collaboratore responsabile vendita</Label>
                <Select value={form.collaboratore_id || "none"} disabled={ro} onValueChange={(v) => set("collaboratore_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="progetto-collaboratore"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuno</SelectItem>{(collaboratori || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5"><Label>Descrizione</Label><Textarea data-testid="progetto-descrizione" disabled={ro} rows={3} value={form.descrizione} onChange={(e) => set("descrizione", e.target.value)} placeholder="L'esperienza, il target, cosa è incluso…" /></div>
            {isTitolare && (
              <div className="grid gap-1.5"><Label>Colore</Label><input data-testid="progetto-colore" type="color" value={form.colore} onChange={(e) => set("colore", e.target.value)} className="h-10 w-20 rounded-lg border border-slate-200 dark:border-slate-800 cursor-pointer bg-transparent" /></div>
            )}
          </TabsContent>

          <TabsContent value="programma" className="pt-3">
            <p className="text-xs text-slate-500 mb-3">Le tappe della giornata: shooting, visita guidata, aperitivo…</p>
            <RowList tid="exp-prog" readOnly={ro} rows={form.programma || []} setRows={(r) => set("programma", r)} addLabel="Aggiungi tappa"
              fields={[{ k: "ora", ph: "Ora", w: "w-24" }, { k: "attivita", ph: "Attività (es. Visita guidata)", w: "flex-1" }]} />
          </TabsContent>

          <TabsContent value="sponsor" className="pt-3">
            <p className="text-xs text-slate-500 mb-3">Enti, associazioni, convention e sponsor che partecipano al progetto.</p>
            <RowList tid="exp-sponsor" readOnly={ro} rows={form.sponsor || []} setRows={(r) => set("sponsor", r)} addLabel="Aggiungi partner"
              fields={[{ k: "nome", ph: "Nome", w: "flex-1" }, { k: "tipo", options: SPONSOR_TIPI, w: "w-36" }, { k: "contributo", ph: "Contributo €", type: "number", w: "w-28" }]} />
            {isTitolare && <div className="text-sm text-right mt-3 text-slate-500">Totale contributi: <b className="text-slate-900 dark:text-white tabular-nums">{eur(totSponsor)}</b></div>}
          </TabsContent>

          <TabsContent value="partecipanti" className="pt-3">
            <p className="text-xs text-slate-500 mb-3">I clienti collegati a questa experience dalla Pipeline (Clienti Privati → campo "Portrait Experience"). Per ricevere l'invito serve l'email nella scheda cliente.</p>
            {isTitolare && initial?.id && (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-primary/5 border border-primary/20 p-3 mb-3">
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  {initial.invito_inviato_at ? <>Invito inviato il <b>{new Date(initial.invito_inviato_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</b>. Reinvia dopo modifiche per aggiornare i calendari.</> : "Invia l'invito calendario a tutti gli iscritti e al collaboratore responsabile."}
                </div>
                <Button data-testid="exp-invite-button" size="sm" className="rounded-full gap-1.5 shrink-0" disabled={saving || conEmail === 0} onClick={() => onInvite(form)}>
                  <Send className="h-3.5 w-3.5" /> {initial.invito_inviato_at ? "Reinvia" : "Invia invito"} ({conEmail})
                </Button>
              </div>
            )}
            {(partecipanti || []).length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">Nessun partecipante ancora.</p> : (
              <div className="space-y-1.5">
                {initial?.invito_inviato_at && (() => {
                  const mancano = partecipanti.filter((p) => p.email && !rsvp[p.email.trim().toLowerCase()]);
                  return mancano.length > 0 && (
                    <div data-testid="exp-mancano" className="text-xs rounded-lg bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 px-3 py-2 mb-2">
                      <b>Mancano {mancano.length} risposte:</b> {mancano.map((p) => p.nome).join(", ")}
                    </div>
                  );
                })()}
                {partecipanti.map((p) => (
                  <div key={p.id} data-testid={`exp-partecipante-${p.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 dark:border-slate-800 px-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium">{p.nome}</div>
                      <div className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Mail className="h-3 w-3" />{p.email || "email mancante"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {initial?.invito_inviato_at && p.email && <RispostaBadge tid={`exp-risposta-${p.id}`} risposta={rsvp[(p.email || "").trim().toLowerCase()]} />}
                      <span className={`text-xs ${p.stage === "chiuso" ? "text-emerald-600 font-semibold" : "text-slate-400"}`}>{p.stage === "chiuso" ? "Pagato" : "Iscritto"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {isTitolare && (
            <TabsContent value="costi" className="pt-3">
              <RowList tid="exp-costi" rows={form.costi || []} setRows={(r) => set("costi", r)} addLabel="Aggiungi costo"
                fields={[{ k: "voce", ph: "Voce (es. Affitto location, catering)", w: "flex-1" }, { k: "importo", ph: "€", type: "number", w: "w-28" }]} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="exp-margine-box">
                {[["Incassi posti", incassi], ["Contributi", totSponsor], ["Costi", -totCosti], ["Margine", incassi + totSponsor - totCosti]].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-slate-50 dark:bg-slate-900/50 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div>
                    <div className={`text-lg font-extrabold tabular-nums ${l === "Margine" ? (v >= 0 ? "text-emerald-600" : "text-rose-600") : "text-slate-900 dark:text-white"}`}>{eur(v)}</div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}
        </Tabs>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)}>{ro ? "Chiudi" : "Annulla"}</Button>
          {!ro && <Button data-testid="progetto-save" className="rounded-full gap-1.5" disabled={saving} onClick={() => onSave(form)}><Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva"}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
